import { useMemo } from "react";
import { CaretRight } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";

interface BreadcrumbBarProps {
  content: string;
  docType: "latex" | "markdown" | "docx";
  currentLine: number;
  onNavigate?: (line: number) => void;
  className?: string;
}

interface Heading {
  title: string;
  level: number;
  line: number;
}

function parseHeadings(content: string, docType: string): Heading[] {
  const lines = content.split("\n");
  const headings: Heading[] = [];

  const latexRegex = /\\(chapter|section|subsection|subsubsection)\*?\{([^}]*)\}/;
  const mdRegex = /^(#{1,6})\s+(.+)/;
  const htmlRegex = /<h([1-6])[^>]*>(.*?)<\/h\1>/i;

  const levelMap: Record<string, number> = {
    chapter: 0,
    section: 1,
    subsection: 2,
    subsubsection: 3,
  };

  for (let i = 0; i < lines.length; i++) {
    if (docType === "latex") {
      const match = latexRegex.exec(lines[i]);
      if (match) {
        headings.push({
          title: match[2].trim(),
          level: levelMap[match[1]] ?? 1,
          line: i + 1,
        });
      }
    } else if (docType === "docx") {
      const match = htmlRegex.exec(lines[i]);
      if (match) {
        headings.push({
          title: match[2].replace(/<[^>]*>/g, "").trim(),
          level: parseInt(match[1]) - 1,
          line: i + 1,
        });
      }
    } else {
      const match = mdRegex.exec(lines[i]);
      if (match) {
        headings.push({
          title: match[2].trim(),
          level: match[1].length - 1,
          line: i + 1,
        });
      }
    }
  }

  return headings;
}

export function BreadcrumbBar({
  content,
  docType,
  currentLine,
  onNavigate,
  className,
}: BreadcrumbBarProps) {
  const breadcrumbs = useMemo(() => {
    const headings = parseHeadings(content, docType);
    const crumbs: Heading[] = [];

    for (const h of headings) {
      if (h.line > currentLine) break;

      while (crumbs.length > 0 && crumbs[crumbs.length - 1].level >= h.level) {
        crumbs.pop();
      }
      crumbs.push(h);
    }

    return crumbs;
  }, [content, docType, currentLine]);

  if (breadcrumbs.length === 0) return null;

  return (
    <div
      className={cn(
        "flex items-center gap-1 px-3 py-1 text-[11px] text-[var(--text-muted)]",
        "border-b border-[var(--border-hairline)] bg-[var(--bg-surface)]/50",
        "overflow-x-auto whitespace-nowrap",
        className,
      )}
    >
      {breadcrumbs.map((crumb, i) => (
        <span key={crumb.line} className="flex items-center gap-1">
          {i > 0 && (
            <CaretRight size={9} weight="bold" className="text-[var(--text-muted)]/50" />
          )}
          <button
            onClick={() => onNavigate?.(crumb.line)}
            className="hover:text-[var(--text-primary)] transition-colors truncate max-w-[150px]"
          >
            {crumb.title}
          </button>
        </span>
      ))}
    </div>
  );
}
